import React from 'react'
import { Edit2, LogoutCurve, More, UserAdd } from 'iconsax-react'
import { useTheme } from '@mui/material'

import ButtonMenu from '../../../../molecules/ButtonMenu'
import RenameConversationModal from '../../../../molecules/ChatModals/RenameConversationModal'
import MoveConversationModal from '../../../../molecules/ChatModals/MoveConversationModal'

import { useAddRemoveParticipant } from '@SonarCX/providers/AddRemoveParticipantProvider'
import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'
import useUserPermissions from '@SonarCX/hooks/permissions/useUserPermissions'
import useStyles from './styles'

const RENAME_CHAT = 'Rename Conversation'
const EDIT_USERS = 'Add/Remove Participants'
const LEAVE_CHAT = 'Leave Conversation'

type PeerChatActionsProps = {
  disable?: boolean
}

const PeerChatActions: React.FC<PeerChatActionsProps> = ({ disable }) => {
  const theme = useTheme()

  const { setEditSessionUsers, setLeave } = useAddRemoveParticipant()
  const { isExtraSmall } = useBreakpoint()
  const { hasBasic } = useUserPermissions()

  const targetRef = React.useRef()
  const [renaming, setRenaming] = React.useState(false)
  const [assigning, setAssigning] = React.useState(false)
  const classes = useStyles({ smallMedia: isExtraSmall })

  const renameIcon = React.useCallback(
    ({ className }) => {
      return <Edit2 size={theme.size.base} className={className} />
    },
    [theme.size.base]
  )

  const addUserIcon = React.useCallback(
    ({ className }) => {
      return <UserAdd size={theme.size.base} className={className} />
    },
    [theme.size.base]
  )

  const logoutIcon = React.useCallback(
    ({ className }) => {
      return <LogoutCurve size={theme.size.base} className={className} />
    },
    [theme.size.base]
  )

  const OPTIONS = React.useMemo(
    () => ({
      [RENAME_CHAT]: renameIcon,
      [EDIT_USERS]: addUserIcon,
      [LEAVE_CHAT]: logoutIcon
    }),
    [addUserIcon, logoutIcon, renameIcon]
  )

  const options = React.useMemo(() => {
    const copy = { ...OPTIONS }

    if (!hasBasic) {
      delete copy[EDIT_USERS]
    }

    return copy
  }, [hasBasic, OPTIONS])

  const handleSelect = React.useCallback(
    item => {
      if (item === EDIT_USERS) {
        setEditSessionUsers(true)
      } else if (item === LEAVE_CHAT) {
        setLeave(true)
      } else {
        setRenaming(true)
      }
    },
    [setEditSessionUsers, setLeave]
  )

  const renderItem = React.useCallback(
    item => {
      const Icon = options[item]

      return (
        <div className={classes.menuItem}>
          <Icon className={classes.menuIcon} />

          {item}
        </div>
      )
    },
    [classes.menuItem, classes.menuIcon, options]
  )

  return (
    <>
      <div ref={targetRef} className={classes.root}>
        <ButtonMenu
          id='peer-menu'
          options={Object.keys(options)}
          onSelect={handleSelect}
          renderFunc={renderItem}
          origin={{
            anchor: { vertical: 'bottom', horizontal: 'right' },
            transform: { vertical: 'top', horizontal: 'right' }
          }}
          targetRef={targetRef}
          disabledOptions={disable ? Object.keys(options) : []}
        >
          <More size={theme.size.base} className={classes.icon} />
        </ButtonMenu>
      </div>

      {renaming && (
        <RenameConversationModal
          open={renaming}
          onClose={() => setRenaming(false)}
        />
      )}

      {assigning && (
        // @ts-expect-error Typing incorrect
        <MoveConversationModal
          open={assigning}
          onClose={() => setAssigning(false)}
          assigning
        />
      )}
    </>
  )
}

export default PeerChatActions
